import { Link } from 'react-router-dom';
import { Fragment, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import Modal from '../components/Modal.jsx';
import DateRangePicker, { rangeForDays } from '../components/DateRangePicker.jsx';
import { ParamsModal } from '../components/publisher/DemandPartnersTab.jsx';
import DeliverySignalHealthPanel from '../components/demand/DeliverySignalHealthPanel.jsx';

// Demand Connectivity & Management workstream: this page used to be just the network-wide
// catalog (enable/margin/timeout/endpoint). It now combines that configuration state with real
// activity data from daily_demand_partner_stats (already collected by every real auction, already
// surfaced elsewhere on OverviewPage's "Demand partner performance" table) and a reverse lookup
// of which publishers have an explicit override -- all reusing existing data sources, no new
// telemetry system. "Usable" below is a client-side-only computed label; it isn't a new backend
// concept and isn't persisted anywhere.
function usability(partner, stats) {
  if (!partner.enabled) return { tone: 'paused', label: 'Disabled' };
  if (!stats || (stats.calls ?? 0) === 0) return { tone: 'neutral', label: 'No recent activity' };
  if ((stats.responses ?? 0) === 0) return { tone: 'error', label: 'Calling, no bids' };
  return { tone: 'active', label: 'Healthy' };
}

function pct(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

export default function DemandPartnersPage() {
  const toast = useToast();
  const [partners, setPartners] = useState(null);
  const [statsByCode, setStatsByCode] = useState({});
  const [range, setRange] = useState({ ...rangeForDays(30), days: 30 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  // Workstream 1/2: default_params is this row's network-wide default credentials/config (e.g.
  // a generic-oRTB partner's zone id + auth secret -- migrations/020_demand_partner_connector_type.sql).
  // Edited through the SAME MaskedParamsEditor/ParamsModal pattern DemandPartnersTab.jsx already
  // uses for per-publisher params -- reusing the existing credential-hygiene mechanism rather than
  // inventing a second one, per Kunal's "do not create unnecessary new concepts" instruction.
  const [editingCreds, setEditingCreds] = useState(null);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [expandedCode, setExpandedCode] = useState(null);
  // Workstream 2: "add a demand partner without engineering" -- only for rows that reuse an
  // already-registered generic connector (POST /api/demand-partners enforces this server-side
  // too; the client-side restriction here is a UX convenience, not the actual safety boundary).
  const [creating, setCreating] = useState(false);
  // 2026-09-08: load() is called both by the range-change effect below AND by several manual
  // refresh sites (toggleEnabled, deletePartner, credential/param saves elsewhere on this page).
  // With no ordering guard, two overlapping calls -- e.g. picking a custom date range right after
  // a preset, or a toggle's refresh racing a range change -- could resolve out of order, and an
  // older, in-flight request finishing AFTER a newer one would silently clobber the newer,
  // correct numbers with stale ones. That's what made per-range figures on this page (e.g. the
  // per-partner "recent activity" panel) look identical/wrong across different date ranges during
  // investigation, even though the underlying API and SQL date filtering are correct (verified
  // directly against /api/admin/stats/demand-partners with disjoint ranges). Every load() call
  // now tags itself with a monotonically increasing id and only applies its result if it's still
  // the most recent call by the time its response arrives -- a stale response is discarded instead
  // of overwriting fresher state.
  const requestIdRef = useRef(0);

  async function load() {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const [list, stats] = await Promise.all([
        api.listDemandPartners(),
        api.demandPartnerStats(range.from, range.to).catch(() => ({ partners: [] })),
      ]);
      if (requestIdRef.current !== requestId) return; // a newer load() has since started -- discard this stale response
      setPartners(list);
      setStatsByCode(Object.fromEntries((stats.partners || []).map((s) => [s.code, s])));
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setError(err instanceof ApiError ? err.message : 'Could not load demand partners.');
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  async function toggleEnabled(partner) {
    setPendingToggle(partner.code);
    try {
      await api.patchDemandPartner(partner.code, { enabled: !partner.enabled });
      toast.success(`${partner.name} ${!partner.enabled ? 'enabled' : 'disabled'} network-wide.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update partner.');
    } finally {
      setPendingToggle(null);
    }
  }

  // Workstream 2: undo for a row created by mistake. Only offered here for a disabled partner --
  // the server enforces the same rule (409 if enabled, or if it has any real usage/stats history)
  // so this is a UX shortcut, not the actual safety boundary.
  async function deletePartner(partner) {
    if (!window.confirm(`Delete ${partner.name} (${partner.code})? This only works if it's never been used — the server will refuse otherwise.`)) return;
    try {
      await api.deleteDemandPartner(partner.code);
      toast.success(`${partner.name} deleted.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete partner.');
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Demand Partners"
        subtitle="Configuration, publisher usage, and recent activity for every connected demand source."
        actions={
          <>
            <button className="btn btn--primary" onClick={() => setCreating(true)}>
              Add demand partner
            </button>{' '}
            <DateRangePicker from={range.from} to={range.to} activeDays={range.days} onChange={setRange} />
          </>
        }
      />

      {/* Above the catalog, not behind a tab. This answers whether the revenue numbers further
          down this page can be described as verified at all, and a warning an operator has to go
          looking for is one that gets found after the invoice. Loads independently of the table
          below, so a slow stats query never delays it and a failure in one never blanks the other. */}
      <DeliverySignalHealthPanel days={range.days ?? 30} />

      {loading && <LoadingState label="Loading demand partners" />}
      {!loading && error && <EmptyState title="Couldn't load demand partners" message={error} />}
      {!loading && !error && partners && partners.length === 0 && (
        <EmptyState title="No demand partners yet" message="Demand partners are provisioned by BidDigi engineering." />
      )}

      {!loading && !error && partners && partners.length > 0 && (
        <section className="card card--flush">
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Connector</th>
                  <th>Media types</th>
                  <th>Network enabled</th>
                  <th>Usable</th>
                  <th className="num">Margin</th>
                  <th className="num">Timeout</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => {
                  const stats = statsByCode[p.code];
                  const status = usability(p, stats);
                  const expanded = expandedCode === p.code;
                  return (
                    <Fragment key={p.code}>
                      <tr
                        key={p.code}
                        className="data-table__row--expandable"
                        onClick={() => setExpandedCode(expanded ? null : p.code)}
                      >
                        <td className="cell-title">
                          <span className={`disclosure${expanded ? ' disclosure--open' : ''}`} aria-hidden="true">
                            &#9656;
                          </span>
                          {p.name}
                          <div className="cell-subtitle">{p.code}</div>
                        </td>
                        <td className="cell-muted">
                          {/* Workstream 1: rows sharing a generic connector (e.g. all 5 Vidverto
                              zones on 'generic_ortb') show that shared name here, so an operator
                              can see at a glance which rows are config-only additions rather than
                              a bespoke connector file. Null (every partner before this migration)
                              reads as "bespoke" -- its own code IS its connector. */}
                          {p.connector_type || <span className="cell-subtitle">bespoke</span>}
                        </td>
                        <td className="cell-muted">{(p.media_types || []).join(', ') || '—'}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={!!p.enabled}
                              disabled={pendingToggle === p.code}
                              onChange={() => toggleEnabled(p)}
                            />
                            <span className="switch__track">
                              <span className="switch__thumb" />
                            </span>
                          </label>
                        </td>
                        <td>
                          <StatusBadge status={status.tone === 'active' ? 'active' : status.tone === 'error' ? 'error' : status.tone === 'paused' ? 'paused' : 'archived'} />
                          {' '}
                          <span className="cell-muted">{status.label}</span>
                        </td>
                        <td className="num">{p.margin_pct}%</td>
                        <td className="num">{p.timeout_ms}ms</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {/* Migrations 039 + 074: the full onboarding workflow -- declarations,
                              conditional reporting config, endpoint verification and activation.
                              The inline Edit modal below still covers the three fields it always
                              did; everything else used to require curl. */}
                          <Link className="btn btn--ghost btn--sm" to={`/demand-partners/${p.code}/onboarding`}>
                            Onboarding
                          </Link>{' '}
                          <button className="btn btn--ghost btn--sm" onClick={() => setEditing(p)}>
                            Edit
                          </button>{' '}
                          <button className="btn btn--ghost btn--sm" onClick={() => setEditingCreds(p)}>
                            Default credentials
                          </button>
                          {!p.enabled && (
                            <>
                              {' '}
                              <button className="btn btn--ghost btn--sm" onClick={() => deletePartner(p)}>
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="data-table__expansion-row">
                          <td colSpan={8}>
                            <PartnerExpansion partner={p} stats={stats} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {editing && (
        <EditPartnerModal
          partner={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {editingCreds && (
        <ParamsModal
          title={`${editingCreds.name} — default credentials (network-wide)`}
          existingKeys={Object.keys(editingCreds.default_params || {})}
          onClose={() => setEditingCreds(null)}
          onSave={async (params) => {
            await api.patchDemandPartner(editingCreds.code, { default_params: params });
            toast.success('Default credentials updated.');
            setEditingCreds(null);
            load();
          }}
        />
      )}

      {creating && (
        <CreatePartnerModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function PartnerExpansion({ partner, stats }) {
  const [publishers, setPublishers] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.listDemandPartnerPublishers(partner.code)
      .then((rows) => { if (!cancelled) setPublishers(rows); })
      .catch((err) => { if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load publisher usage.'); });
    return () => { cancelled = true; };
  }, [partner.code]);

  return (
    <div className="placement-expansion placement-expansion--wide">
      <div className="placement-expansion__block">
        <h3 className="placement-expansion__heading">Recent activity ({stats ? 'this range' : 'no data yet'})</h3>
        {stats ? (
          <p className="card__hint">
            {(stats.calls ?? 0).toLocaleString()} calls · {(stats.responses ?? 0).toLocaleString()} responses ({pct(stats.responseRate)}) ·{' '}
            {(stats.wins ?? 0).toLocaleString()} wins ({pct(stats.winRate)})
          </p>
        ) : (
          <p className="card__hint">No auctions have called this partner in the selected date range.</p>
        )}
      </div>
      <div className="placement-expansion__block">
        <h3 className="placement-expansion__heading">Publishers with an explicit override</h3>
        {error && <p className="card__hint">{error}</p>}
        {!error && publishers === null && <p className="card__hint">Loading…</p>}
        {!error && publishers && publishers.length === 0 && (
          <p className="card__hint">
            No publisher has an override for this partner — every publisher currently relies on the network-default
            endpoint/credentials above (or has this partner disabled at the publisher level).
          </p>
        )}
        {!error && publishers && publishers.length > 0 && (
          <ul className="plain-list">
            {publishers.map((pub) => (
              <li key={pub.publisher_id}>
                {pub.publisher_name} <span className="cell-muted">({pub.publisher_domain})</span>{' '}
                <StatusBadge status={pub.enabled ? 'active' : 'paused'} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EditPartnerModal({ partner, onClose, onSaved }) {
  const toast = useToast();
  const [marginPct, setMarginPct] = useState(String(partner.margin_pct));
  const [timeoutMs, setTimeoutMs] = useState(String(partner.timeout_ms));
  const [endpointUrl, setEndpointUrl] = useState(partner.endpoint_url || '');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    const margin = Number(marginPct);
    const timeout = Number(timeoutMs);
    if (Number.isNaN(margin) || margin < 0 || margin > 100) {
      setFormError('Margin must be a number between 0 and 100.');
      return;
    }
    if (Number.isNaN(timeout) || timeout <= 0) {
      setFormError('Timeout must be a positive number of milliseconds.');
      return;
    }
    setSaving(true);
    try {
      await api.patchDemandPartner(partner.code, {
        margin_pct: margin,
        timeout_ms: timeout,
        endpoint_url: endpointUrl.trim(),
      });
      toast.success(`${partner.name} updated.`);
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not update partner.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`Edit ${partner.name}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <label className="field">
          <span className="field__label">Margin (%)</span>
          <input type="number" min="0" max="100" step="0.1" value={marginPct} onChange={(e) => setMarginPct(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Timeout (ms)</span>
          <input type="number" min="1" value={timeoutMs} onChange={(e) => setTimeoutMs(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Endpoint URL</span>
          <input value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} placeholder="https://partner.example.com/bid" />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Workstream 2: the design test from Kunal's mandate is "if tomorrow I give you another standard
// oRTB endpoint, how much code should need to change? The answer should ideally be:
// configuration, not a new connector." This modal is that configuration surface -- it can only
// create rows whose connector_type reuses an already-registered generic connector (currently just
// 'generic_ortb', src/connectors/genericOrtbConnector.js in auction-service). A brand-new bespoke
// integration still needs an engineer to write a connector file + migration, same as before; this
// does not and should not replace that path. New rows always come back disabled (enforced
// server-side too) -- real credentials (default_params) must be set via "Default credentials"
// and verified before flipping a new partner live, so this modal deliberately does not ask for
// them: mixing a credential field into a "create the row" form is exactly the kind of secret-in-
// a-generic-form-state risk Kunal's mandate warns against, and the existing MaskedParamsEditor/
// ParamsModal flow already does this correctly.
const SELF_SERVE_CONNECTOR_TYPES = ['generic_ortb'];
const MEDIA_TYPE_OPTIONS = ['banner', 'video', 'native'];

function CreatePartnerModal({ onClose, onCreated }) {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [connectorType, setConnectorType] = useState(SELF_SERVE_CONNECTOR_TYPES[0]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [endpointUrl, setEndpointUrl] = useState('');
  const [marginPct, setMarginPct] = useState('0');
  const [timeoutMs, setTimeoutMs] = useState('700');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  function toggleMediaType(mt) {
    setMediaTypes((prev) => (prev.includes(mt) ? prev.filter((x) => x !== mt) : [...prev, mt]));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);

    const trimmedCode = code.trim();
    if (!trimmedCode || !/^[a-z][a-z0-9_]*$/.test(trimmedCode)) {
      setFormError('Code is required and must be lowercase snake_case (e.g. "vidverto_web_banner").');
      return;
    }
    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (mediaTypes.length === 0) {
      setFormError('Select at least one media type.');
      return;
    }
    if (!endpointUrl.trim()) {
      setFormError('Endpoint URL is required — this connector has no built-in default.');
      return;
    }
    const margin = Number(marginPct);
    const timeout = Number(timeoutMs);
    if (Number.isNaN(margin) || margin < 0 || margin > 100) {
      setFormError('Margin must be a number between 0 and 100.');
      return;
    }
    if (Number.isNaN(timeout) || timeout <= 0) {
      setFormError('Timeout must be a positive number of milliseconds.');
      return;
    }

    setSaving(true);
    try {
      await api.createDemandPartner({
        code: trimmedCode,
        name: name.trim(),
        connector_type: connectorType,
        media_types: mediaTypes,
        endpoint_url: endpointUrl.trim(),
        margin_pct: margin,
        timeout_ms: timeout,
      });
      toast.success(`${name.trim()} created — disabled by default. Set default credentials, then enable it.`);
      onCreated();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not create demand partner.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Add demand partner" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <p className="card__hint">
          Creates a new row that calls out through an existing, already-registered connector — no
          engineering change needed. A brand-new bespoke integration (a partner with its own
          non-standard protocol) still requires a connector file and isn't available here.
        </p>
        <label className="field">
          <span className="field__label">Code</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. vidverto_web_banner"
            autoFocus
          />
        </label>
        <label className="field">
          <span className="field__label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vidverto — Web Banner" />
        </label>
        <label className="field">
          <span className="field__label">Connector</span>
          <select value={connectorType} onChange={(e) => setConnectorType(e.target.value)}>
            {SELF_SERVE_CONNECTOR_TYPES.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          <span className="field__label">Media types</span>
          <div className="checkbox-row">
            {MEDIA_TYPE_OPTIONS.map((mt) => (
              <label key={mt} className="field field--checkbox field--checkbox-inline">
                <input type="checkbox" checked={mediaTypes.includes(mt)} onChange={() => toggleMediaType(mt)} />
                <span>{mt}</span>
              </label>
            ))}
          </div>
        </div>
        <label className="field">
          <span className="field__label">Endpoint URL</span>
          <input value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} placeholder="https://partner.example.com/rtb" />
        </label>
        <label className="field">
          <span className="field__label">Margin (%)</span>
          <input type="number" min="0" max="100" step="0.1" value={marginPct} onChange={(e) => setMarginPct(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Timeout (ms)</span>
          <input type="number" min="1" value={timeoutMs} onChange={(e) => setTimeoutMs(e.target.value)} />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create (disabled)'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
